import React, { useRef, lazy } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { ScrollReveal } from './ScrollReveal';
interface PageConfig {
  path: string;
  label: string;
  image: string;
}
const navigationSequence: PageConfig[] = [{
  path: '/',
  label: 'Accueil',
  image: 'https://cdn.magicpatterns.com/uploads/43KQ18pUaeaDRRH3kfVWeb/IMG_2664-PROOF-THOMAS-MASOTTI.jpg'
}, {
  path: '/avant-apres',
  label: 'Avant / Après',
  image: 'https://cdn.magicpatterns.com/uploads/qLiEZSrZysNdc46pRYdzD8/Posnetek_zaslona_2026-04-28_100056.png'
}, {
  path: '/tarifs',
  label: 'Tarifs',
  image: 'https://cdn.magicpatterns.com/uploads/tunERiVhYWHGBKBfMUodbr/IMG_2775-PROOF-THOMAS-MASOTTI.jpg'
}, {
  path: '/technologie-laser',
  label: 'Technologie Laser',
  image: 'https://cdn.magicpatterns.com/uploads/gPuGLFxqLPD9w6gA9pCBvR/IMG_2731-PROOF-THOMAS-MASOTTI.jpg'
}, {
  path: '/faq',
  label: 'FAQ',
  image: 'https://cdn.magicpatterns.com/uploads/k8gz3yZyWgvaCAXRy24LsG/IMG_2687-PROOF-THOMAS-MASOTTI.jpg'
}, {
  path: '/blog',
  label: 'Blog',
  image: 'https://cdn.magicpatterns.com/uploads/c7DtgUV4minxineBKqyaMP/IMG_2681-PROOF-THOMAS-MASOTTI.jpg'
}, {
  path: '/contact',
  label: 'Contact',
  image: 'https://cdn.magicpatterns.com/uploads/izZHwqrQkC9cpfBcRpymy9/IMG_2742-PROOF-THOMAS-MASOTTI.jpg'
}];
export const NextPageLink = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const ref = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.1, 1, 1]);
  // Find current page index
  const currentIndex = navigationSequence.findIndex(page => page.path === location.pathname);
  // Get next page (wrap around to start)
  const nextIndex = (currentIndex + 1) % navigationSequence.length;
  const nextPage = navigationSequence[nextIndex];
  // Per-source-page image overrides (when we want a different visual for the
  // "next page" teaser depending on which page the user is currently on)
  const sourcePageImageOverrides: Record<string, string> = {
    '/': 'https://cdn.magicpatterns.com/uploads/c7DtgUV4minxineBKqyaMP/IMG_2681-PROOF-THOMAS-MASOTTI.jpg'
  };
  const displayImage = sourcePageImageOverrides[location.pathname] ?? nextPage.image;
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(nextPage.path);
  };
  return <section ref={ref} className="relative min-h-[40vh] md:min-h-[50vh] flex items-center justify-center overflow-hidden cursor-pointer group" data-id="element-143">
      {/* Background Image with Parallax Zoom */}
      <motion.div style={{
      scale
    }} className="absolute inset-0 w-full h-full" data-id="element-144">
        <img src={displayImage} alt="" className="w-full h-full object-cover grayscale" loading="lazy" data-id="element-145" />
      </motion.div>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-brand-darker via-brand-dark/80 to-brand-dark/60 group-hover:from-brand-darker/90 group-hover:via-brand-dark/70 group-hover:to-brand-dark/50 transition-all duration-500" data-id="element-146" />

      {/* Hover Scale Effect on Background */}
      <motion.div className="absolute inset-0" whileHover={{
      scale: 1.03
    }} transition={{
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1]
    }} data-id="element-147" />

      {/* Content */}
      <Link to={nextPage.path} onClick={handleClick} className="relative z-10 text-center px-6 md:px-12 w-full" data-id="element-148">
        <ScrollReveal delay={0.1} data-id="element-149">
          <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-brand-gray mb-4 md:mb-6 font-medium" data-id="element-150">
            Continuer vers
          </p>
        </ScrollReveal>

        <ScrollReveal delay={0.2} blur scale data-id="element-151">
          <h2 className="text-5xl md:text-5xl lg:text-6xl xl:text-7xl font-medium tracking-tight text-brand-light mb-6 md:mb-8 leading-[0.95] text-balance" data-id="element-152">
            {nextPage.label}
          </h2>
        </ScrollReveal>

        <ScrollReveal delay={0.3} data-id="element-153">
          <motion.div className="inline-flex items-center justify-center" whileHover={{
          x: 8
        }} transition={{
          duration: 0.3
        }} data-id="element-154">
            <ArrowRight className="w-8 h-8 md:w-10 md:h-10 text-brand-light" data-id="element-155" />
          </motion.div>
        </ScrollReveal>
      </Link>
    </section>;
};